import { Plus } from 'lucide-react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { TableSearch } from '../../components/form/Fields';
import { usePropertyImagePreview, UserInital } from '../../utils/otherHelopers';
import ConfirmModal from '../../components/withModal/ConfirmModal';
import { useToast } from '../../context/TostContext';
import { useLoader } from '../../context/LoaderContext';
import StatusChip from '../../components/statusChip/StatusChip';
import CustomTable from '../../components/table/CustomTable';
import CommonButton from '../../components/commonButton';
import { routes } from '../../routes/pageRoutes';
import Axios, { authHeaders } from '../../utils/axios';
import { formatMomentDate } from '../../utils/dateHelper';

const AllPublicProperties = ({ isLand }) => {
    const navigate = useNavigate();
    const navigationType = useNavigationType();

    const [searchTerm, setSearchTerm] = useState(() => {
        if (navigationType === "POP") {
            const savedSearch = sessionStorage.getItem(isLand ? "publicLands_search" : "publicProperties_search");
            if (savedSearch) return savedSearch;
        }
        return '';
    });

    const [currentPage, setCurrentPage] = useState(() => {
        if (navigationType === "POP") {
            const savedPage = sessionStorage.getItem(isLand ? "publicLands_page" : "publicProperties_page");
            if (savedPage) return parseInt(savedPage, 10);
        }
        return 1;
    });

    const [pageSize, setPageSize] = useState(() => {
        if (navigationType === "POP") {
            const savedLimit = sessionStorage.getItem(isLand ? "publicLands_limit" : "publicProperties_limit");
            if (savedLimit) return parseInt(savedLimit, 10);
        }
        return 10;
    });

    const [propertiesList, setPropertiesList] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const { renderCell: renderImageCell, renderPreviewModal } = usePropertyImagePreview();
    const toast = useToast();
    const { setLoading: setGlobalLoading } = useLoader();
    const [confirmModalConfig, setConfirmModalConfig] = useState({
        isOpen: false,
        title: "",
        message: "",
        onConfirm: () => { },
        confirmText: "Yes",
        cancelText: "No",
    });

    useEffect(() => {
        sessionStorage.setItem(isLand ? "publicLands_page" : "publicProperties_page", currentPage.toString());
        sessionStorage.setItem(isLand ? "publicLands_limit" : "publicProperties_limit", pageSize.toString());
        sessionStorage.setItem(isLand ? "publicLands_search" : "publicProperties_search", searchTerm);
    }, [currentPage, pageSize, searchTerm, isLand]);

    const fetchProperties = useCallback(async () => {
        setLoading(true);
        try {
            const response = await Axios.get(`/broker/properties/public-properties`, {
                params: {
                    page: currentPage,
                    limit: pageSize,
                    search: searchTerm,
                    is_land: isLand ? "true" : "false"
                },
                ...authHeaders()
            });
            const result = response.data?.data;
            setPropertiesList(result?.data || result?.rows || result || []);
            setTotal(response.data?.meta?.total || response.data?.data?.meta?.total || 0);
        } catch (error) {
            console.error(`Failed to fetch public ${isLand ? 'lands' : 'properties'}:`, error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchTerm, isLand]);

    useEffect(() => {
        fetchProperties();
    }, [fetchProperties]);

    const handleViewProperty = (row) => {
        const detailRoute = isLand ? routes.viewPublicLand : routes.viewPublicProperty;
        navigate(`${detailRoute.replace(":id", row.id)}`, {
            state: {
                id: row.id,
                is_public: true
            }
        });
    };


    const handleEditProperty = (row) => {
        const isLinkedPublic = !!row.property_id;
        const isStandalonePublic = !row.property_id;
        const editId = isLinkedPublic ? row.property_id : row.id;
        const editRoute = isLand ? routes.editPublicLand : routes.editPublicProperty;

        navigate(`${editRoute.replace(":id", editId)}`, {
            state: {
                item: row,
                isEdit: true,
                isPublicProperty: isStandalonePublic,
                fromGlobalPublic: true,
                isLand: isLand
            }
        });
    };

    const handleTogglePropertyStatus = (row) => {
        const newStatus = row.status === "Open" ? "Close" : "Open";
        setConfirmModalConfig({
            isOpen: true,
            title: `Confirm Status Change`,
            message: `Are you sure you want to change the status of this property to "${newStatus}"?`,
            confirmText: "Yes",
            cancelText: "No",
            onConfirm: async () => {
                setConfirmModalConfig(prev => ({ ...prev, isOpen: false }));
                setGlobalLoading(true);
                try {
                    const response = await Axios.patch(`/broker/properties/public-properties/${row.id}/change-status`, { status: newStatus }, authHeaders());
                    toast.success(response.data?.message || "Status updated successfully.");
                    setPropertiesList(prev => prev.map(item => item.id === row.id ? { ...item, status: newStatus, updated_at: new Date().toISOString() } : item));
                } catch (error) {
                    toast.error(error.response?.data?.message || error.message || "Failed to update status.");
                } finally {
                    setGlobalLoading(false);
                }
            }
        });
    };

    const handleTogglePropertyArchive = (row) => {
        const isArchived = row.is_archived;
        const actionName = isArchived ? "unarchive" : "archive";
        setConfirmModalConfig({
            isOpen: true,
            title: `Confirm ${isArchived ? "Unarchive" : "Archive"}`,
            message: `Are you sure you want to ${actionName} this property?`,
            confirmText: "Yes",
            cancelText: "No",
            onConfirm: async () => {
                setConfirmModalConfig(prev => ({ ...prev, isOpen: false }));
                setGlobalLoading(true);
                try {
                    const response = await Axios.patch(`/broker/properties/public-properties/${row.id}/${actionName}`, {}, authHeaders());
                    toast.success(response.data?.message || `Property successfully ${isArchived ? 'unarchived' : 'archived'}.`);
                    setPropertiesList(prev => prev.map(item => item.id === row.id ? { ...item, is_archived: !isArchived, updated_at: new Date().toISOString() } : item));
                } catch (error) {
                    toast.error(error.response?.data?.message || error.message || `Failed to ${actionName} property.`);
                } finally {
                    setGlobalLoading(false);
                }
            }
        });
    };

    const handleDeleteProperty = (row) => {
        setConfirmModalConfig({
            isOpen: true,
            title: "Confirm Delete",
            message: "Are you sure you want to delete this property? This action cannot be undone.",
            confirmText: "Delete",
            cancelText: "Cancel",
            onConfirm: async () => {
                setConfirmModalConfig(prev => ({ ...prev, isOpen: false }));
                setGlobalLoading(true);
                try {
                    const response = await Axios.delete(`/broker/properties/public-properties/${row.id}`, authHeaders());
                    toast.success(response.data?.message || "Property deleted successfully.");
                    setPropertiesList(prev => prev.filter(item => item.id !== row.id));
                    setTotal(prev => Math.max(0, prev - 1));
                } catch (error) {
                    toast.error(error.response?.data?.message || error.message || "Failed to delete property.");
                } finally {
                    setGlobalLoading(false);
                }
            }
        });
    };

    const actionList = [
        {
            title: 'View',
            handler: handleViewProperty
        },
        {
            title: 'Edit',
            handler: handleEditProperty
        },
        {
            title: (row) => row.status === 'Open' ? 'Close' : 'Open',
            handler: handleTogglePropertyStatus
        },
        {
            title: (row) => row.is_archived ? 'Unarchive' : 'Archive',
            handler: handleTogglePropertyArchive
        },
        {
            title: 'Delete',
            handler: handleDeleteProperty
        }
    ];

    const columns = useMemo(() => {
        if (isLand) {
            return [
                {
                    id: 'image',
                    header: 'IMAGES',
                    accessor: 'photos',
                    cell: (_, row) => renderImageCell(row)
                },
                {
                    id: 'user',
                    header: 'USER',
                    cell: (_, row) => {
                        const userName = row.user ? `${row.user.first_name || ""} ${row.user.last_name || ""}`.trim() : "—";
                        return (
                            <div className="d-flex align-items-center gap-2">
                                <UserInital image={row.user?.profile?.profile_photo?.url} name={userName} />
                                <div>
                                    <h6 className="fw-semibold fs-md">{userName || "—"}</h6>
                                    <span className="text-muted fs-sm">{row.user?.mobile_number || "—"}</span>
                                    <p className="text-muted fs-sm">{row.user?.email || "—"}</p>
                                </div>
                            </div>
                        );
                    }
                },
                {
                    id: 'client',
                    header: 'CLIENT',
                    accessor: 'client',
                    cell: (value) => (
                        <div className="d-flex flex-column text-start">
                            <span className="fw-semibold text-dark text-nowrap">{value?.name}</span>
                            <span className="small text-muted" style={{ fontSize: '11px' }}>{value?.mobile_number || "—"}</span>
                            <span className="small text-muted" style={{ fontSize: '11px' }}>{value?.email || "—"}</span>
                        </div>
                    )
                },
                {
                    id: 'clientType',
                    header: 'CLIENT TYPE',
                    accessor: 'client_type',
                },
                {
                    id: 'transactionType',
                    header: 'TRANSACTION TYPE',
                    accessor: 'transaction_type',
                },
                {
                    id: 'landType',
                    header: 'LAND TYPE',
                    accessor: 'land_type',
                    cell: (v) => <span className="fw-semibold text-dark">{v || "—"}</span>
                },
                {
                    id: 'tenure',
                    header: 'TENURE',
                    accessor: 'tenure',
                },
                {
                    id: 'location',
                    header: 'LOCATION',
                    cell: (_, row) => {
                        const locationText = [row.village?.name, row.taluka?.name, row.district?.name, row.state?.name]
                            .filter(Boolean)
                            .join(", ");
                        return <span className="small text-muted">{locationText || "—"}</span>;
                    }
                },
                {
                    id: 'landArea',
                    header: 'LAND AREA',
                    accessor: 'land_area',
                },
                {
                    id: 'landAreaUnit',
                    header: 'LAND AREA UNIT',
                    accessor: 'land_area_unit',
                },
                {
                    id: 'pricePerUnit',
                    header: 'PRICE PER UNIT',
                    accessor: 'price_per_unit',
                },
                {
                    id: 'priceUnit',
                    header: 'PRICE UNIT',
                    accessor: 'price_unit',
                },
                {
                    id: 'totalPrice',
                    header: 'TOTAL PRICE',
                    accessor: 'total_price',
                },
                {
                    id: 'status',
                    header: 'STATUS',
                    accessor: 'status',
                    cell: (v) => {
                        return v ? (
                            <StatusChip
                                bg={
                                    v === "Close"
                                        ? "warning"
                                        : v === "Live"
                                            ? "success"
                                            : v === "Open"
                                                ? "blue"
                                                : v === "Expired"
                                                    ? "danger"
                                                    : ""
                                }
                                pill
                                title={v}
                                className="text-capitalize"
                            />
                        ) : "—";
                    }
                },
                {
                    id: 'isArchived',
                    header: 'IS ARCHIVED',
                    accessor: 'is_archived',
                    cell: (v) => (
                        <StatusChip
                            bg={v ? "danger" : "success"}
                            pill
                            title={v ? "Yes" : "No"}
                        />
                    )
                },
                {
                    id: 'linkedToPrivate',
                    header: 'LINKED TO PRIVATE',
                    accessor: 'property_id',
                    cell: (v) => (
                        <StatusChip
                            bg={v ? "success" : "danger"}
                            pill
                            title={v ? "Yes" : "No"}
                        />
                    )
                },
                {
                    id: 'createdAt',
                    header: 'CREATED AT',
                    accessor: 'created_at',
                    cell: (v) => formatMomentDate(v, "DD-MM-YYYY hh:mm A")
                },
                {
                    id: 'updatedAt',
                    header: 'UPDATED AT',
                    accessor: 'updated_at',
                    cell: (v) => formatMomentDate(v, "DD-MM-YYYY hh:mm A")
                }
            ];
        }

        // Columns for properties (isLand === false)
        return [
            {
                id: 'image',
                header: 'IMAGES',
                accessor: 'files',
                cell: (_, row) => renderImageCell(row)
            },
            {
                id: 'user',
                header: 'USER',
                accessor: "user",
                cell: (user) => {
                    const userName = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : "—";
                    return (
                        <div className="d-flex align-items-center gap-2">
                            <UserInital image={user?.profile?.profile_photo?.url} name={userName} />
                            <div>
                                <h6 className="fw-semibold fs-md">{userName || "—"}</h6>
                                <span className="text-muted fs-sm">{user?.mobile_number || "—"}</span>
                                <p className="text-muted fs-sm">{user?.email || "—"}</p>
                            </div>
                        </div>
                    );
                }
            },
            {
                id: 'client',
                header: 'CLIENT',
                accessor: 'client',
                cell: (v) => (
                    <div className="d-flex flex-column text-start">
                        <span className="fw-semibold text-dark text-nowrap">{v?.name}</span>
                        <span className="small text-muted" style={{ fontSize: '11px' }}>{v?.mobile_number || "—"}</span>
                        <span className="small text-muted" style={{ fontSize: '11px' }}>{v?.email || "—"}</span>
                    </div>
                )
            },
            {
                id: 'clientType',
                header: 'CLIENT TYPE',
                accessor: 'client_type'
            },
            {
                id: 'transactionType',
                header: 'TRANSACTION TYPE',
                accessor: 'transaction_type'
            },
            {
                id: 'propertyType',
                header: 'PROPERTY TYPE',
                accessor: 'property_type',
                cell: (v) => <span className="fw-semibold text-dark">{v || "—"}</span>
            },
            {
                id: 'location',
                header: 'LOCATION',
                cell: (_, row) => {
                    const locationText = [row.locality?.name, row.city?.name, row.state?.name]
                        .filter(Boolean)
                        .join(", ");
                    return <span className="small text-muted">{locationText || "—"}</span>;
                }
            },
            {
                id: 'sBuiltupArea',
                header: 'S. BUILTUP AREA',
                accessor: 's_builtup_area'
            },
            {
                id: 'sBuiltupAreaUnit',
                header: 'S. BUILTUP AREA UNIT',
                accessor: 's_builtup_area_unit'
            },
            {
                id: 'plotArea',
                header: 'PLOT AREA',
                accessor: 'plot_area'
            },
            {
                id: 'plotAreaUnit',
                header: 'PLOT AREA UNIT',
                accessor: 'plot_area_unit'
            },
            {
                id: 'possession',
                header: 'POSSESSION',
                accessor: 'possession'
            },
            {
                id: 'furnishingStatus',
                header: 'FURNISHING STATUS',
                accessor: 'furnishing_status'
            },
            {
                id: 'pricePerUnit',
                header: 'PRICE PER UNIT',
                accessor: 'price_per_unit'
            },
            {
                id: 'priceUnit',
                header: 'PRICE UNIT',
                accessor: 'price_unit'
            },
            {
                id: 'totalPrice',
                header: 'TOTAL PRICE',
                accessor: 'total_price'
            },
            {
                id: 'status',
                header: 'STATUS',
                accessor: 'status',
                cell: (v) => {
                    return v ? (
                        <StatusChip
                            bg={
                                v === "Close"
                                    ? "warning"
                                    : v === "Live"
                                        ? "success"
                                        : v === "Open"
                                            ? "blue"
                                            : v === "Expired"
                                                ? "danger"
                                                : ""
                            }
                            pill
                            title={v}
                            className="text-capitalize"
                        />
                    ) : "—";
                }
            },
            {
                id: 'isArchived',
                header: 'IS ARCHIVED',
                accessor: 'is_archived',
                cell: (v) => (
                    <StatusChip
                        bg={v ? "danger" : "success"}
                        pill
                        title={v ? "Yes" : "No"}
                    />
                )
            },
            {
                id: 'linkedToPrivate',
                header: 'LINKED TO PRIVATE',
                accessor: 'property_id',
                cell: (v) => (
                    <StatusChip
                        bg={v ? "success" : "danger"}
                        pill
                        title={v ? "Yes" : "No"}
                    />
                )
            },
            {
                id: 'createdAt',
                header: 'CREATED AT',
                accessor: 'created_at',
                cell: (v) => formatMomentDate(v, "DD-MM-YYYY hh:mm A")
            },
            {
                id: 'updatedAt',
                header: 'UPDATED AT',
                accessor: 'updated_at',
                cell: (v) => formatMomentDate(v, "DD-MM-YYYY hh:mm A")
            }
        ];
    }, [isLand, renderImageCell]);

    const totalPages = Math.ceil(total / pageSize);
    const activePage = Math.min(currentPage, Math.max(1, totalPages));

    const headerPortalTarget = document.getElementById("header-portal-target");


    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                {isLand ? "Public Lands" : "Public Properties"}
            </h4>
        </div>
    );
    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <div className="bg-white rounded-2 overflow-hidden common-shadow">
                <div className="d-flex align-items-center justify-content-between gap-4 p-4">
                    <TableSearch
                        value={searchTerm}
                        placeholder="Search"
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        onClear={() => {
                            setSearchTerm('');
                            setCurrentPage(1);
                        }}
                    />
                    <CommonButton
                        icon={<Plus size={16} />}
                        variant="primary"
                        title={isLand ? "Add Public Land" : "Add Public Property"}
                        type="button"
                        onClick={() => {
                            navigate(isLand ? routes.addPublicLand : routes.addPublicProperty, {
                                state: {
                                    isPublicProperty: true,
                                    fromGlobalPublic: true,
                                    isLand: isLand
                                }
                            });
                        }}
                    />
                </div>

                <div className="overflow-hidden">
                    <CustomTable
                        data={propertiesList}
                        columns={columns}
                        keyField="id"
                        actionList={actionList}
                        emptyMessage={`No ${isLand ? 'lands' : 'properties'} found`}
                        loading={loading}
                        rowClick={handleViewProperty}
                        tbody={{ trClass: "cursor-pointer" }}
                        cover={{ mainClass: "user-properties-table" }}
                        pagination={{
                            currentPage: activePage,
                            totalPages,
                            total,
                            onPageChange: (page) => setCurrentPage(page),
                            pageSize,
                            setPageSize: (size) => {
                                setPageSize(size);
                                setCurrentPage(1);
                            },
                            setCurrentPage,
                        }}
                    />
                </div>
                {renderPreviewModal()}
                <ConfirmModal
                    isOpen={confirmModalConfig.isOpen}
                    onClose={() => setConfirmModalConfig((prev) => ({ ...prev, isOpen: false }))}
                    title={confirmModalConfig.title}
                    message={confirmModalConfig.message}
                    onConfirm={confirmModalConfig.onConfirm}
                    confirmText={confirmModalConfig.confirmText}
                    cancelText={confirmModalConfig.cancelText}
                    className="confirm-modal"
                    size="sm"
                />
            </div>
        </>
    );
};

export default AllPublicProperties;
